import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { Field } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { Dismiss12Regular } from '@fluentui/react-icons';
import { useDebounce } from '@/hooks';
import { useLazySearchEmployeesQuery } from '@/store/api';
import type { EmployeeSearchResult } from '@store/models';
import { SearchResultCard } from './SearchResultCard';
import { usePeoplePickerStyles } from './PeoplePicker.styles';

const MAX_RESULTS = 10;
const MIN_CHARS = 3;

interface PeoplePickerProps {
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  error?: string;
  disabled: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export function PeoplePicker({
  value,
  onChange,
  error,
  disabled,
  label,
  placeholder,
  required,
}: PeoplePickerProps) {
  const { t } = useTranslation('bookingForm');
  const styles = usePeoplePickerStyles();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debouncedQuery = useDebounce(query);
  const [triggerSearch, { data, isFetching, isError }] = useLazySearchEmployeesQuery();
  const listboxId = useId();

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedName = value as string | undefined;
  const hasSelection = !!selectedName;

  useEffect(() => {
    if (debouncedQuery.length >= MIN_CHARS && !hasSelection) {
      void triggerSearch(debouncedQuery);
      setIsOpen(true);
      setActiveIndex(-1);
    }
  }, [debouncedQuery, hasSelection, triggerSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const results = data?.Results?.slice(0, MAX_RESULTS) ?? [];

  const handleSelect = useCallback(
    (employee: EmployeeSearchResult) => {
      onChange('sponsor.name', employee.displayName);
      onChange('sponsor.email', employee.userPrincipalName);
      setQuery('');
      setIsOpen(false);
      setActiveIndex(-1);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange('sponsor.name', '');
    onChange('sponsor.email', '');
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  }, [onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (val.length < MIN_CHARS) {
      setIsOpen(val.length > 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < results.length) {
          const activeResult = results[activeIndex];
          if (activeResult) {
            handleSelect(activeResult);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  const getDropdownContent = () => {
    if (isFetching) {
      return <div className={styles.dropdownMessage}>{t('bookingForm.peoplePicker.loading')}</div>;
    }

    if (isError) {
      return <div className={styles.dropdownMessage}>{t('bookingForm.peoplePicker.error')}</div>;
    }

    if (query.length > 0 && query.length < MIN_CHARS) {
      return <div className={styles.dropdownMessage}>{t('bookingForm.peoplePicker.minChars')}</div>;
    }

    if (debouncedQuery.length >= MIN_CHARS && results.length === 0 && !isFetching) {
      return (
        <div className={styles.dropdownMessage}>{t('bookingForm.peoplePicker.noResults')}</div>
      );
    }

    if (results.length > 0) {
      return (
        <div role="listbox">
          {results.map((employee, index) => (
            <SearchResultCard
              key={employee.userPrincipalName}
              displayName={employee.displayName}
              userPrincipalName={employee.userPrincipalName}
              jobTitle={employee.jobTitle}
              officeLocation={employee.officeLocation}
              isActive={index === activeIndex}
              onClick={() => handleSelect(employee)}
            />
          ))}
        </div>
      );
    }

    return null;
  };

  const dropdownContent = isOpen ? getDropdownContent() : null;

  return (
    <Field
      label={label}
      required={required}
      validationMessage={error ?? null}
      validationState={error ? 'error' : 'none'}
    >
      <div
        className={`${styles.root} ${dropdownContent ? styles.rootOpen : ''}`}
        ref={containerRef}
      >
        <div className={styles.inputArea}>
          {dropdownContent && (
            <div className={styles.dropdown} id={listboxId}>
              {dropdownContent}
            </div>
          )}
          {hasSelection ? (
            <div
              className={`${styles.inputWrapper} ${disabled ? styles.inputWrapperDisabled : ''}`}
            >
              <div className={styles.pill}>
                <span>{selectedName}</span>
                {!disabled && (
                  <button
                    type="button"
                    className={styles.pillRemove}
                    onClick={handleClear}
                    aria-label={t('bookingForm.peoplePicker.clearSelection', 'Clear selection')}
                  >
                    <Dismiss12Regular />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <label
              className={`${styles.inputWrapper} ${disabled ? styles.inputWrapperDisabled : ''}`}
            >
              <input
                ref={inputRef}
                className={styles.input}
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (query.length > 0) setIsOpen(true);
                }}
                placeholder={placeholder ?? ''}
                disabled={disabled}
                aria-label={label ?? placeholder ?? t('bookingForm.peoplePicker.placeholder')}
                aria-controls={listboxId}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-autocomplete="list"
                role="combobox"
              />
            </label>
          )}
        </div>
      </div>
    </Field>
  );
}
